/**
 * Query Key Factory
 *
 * Centralized query key management for React Query.
 * This ensures consistent query key structure and makes invalidation easier.
 *
 * Usage:
 *   queryKeys.locations.detail(1) // ['locations', 'detail', 1]
 *   queryKeys.locations.lists()    // ['locations', 'list']
 *
 * Benefits:
 * - Type-safe query keys
 * - Easy invalidation (e.g., invalidate all locations)
 * - Consistent structure across the app
 */

/**
 * Event filter parameters for query key generation
 * When filters change, the query key changes, triggering a refetch
 */
export interface EventFilters {
	endTimestamp?: string;
	locationIds?: string[];
	sensorIds?: string[];
	severityMin?: number;
	sortDirection?: number;
	sortField?: string;
	startTimestamp?: string;
	status?: string;
	title?: string;
	types?: string[];
	watches?: string[];
	zones?: string[];
}

export interface WatchFilters {
	alertCount?: number;
	includeArchived?: boolean;
	location?: string[];
	minAlerts?: number;
	search?: string;
	sortDirection?: number;
	sortField?: string;
	status?: string;
}

export const queryKeys = {
	events: {
		all: ["events"] as const,
		detail: (id: string) => [...queryKeys.events.details(), id] as const,
		details: () => [...queryKeys.events.all, "detail"] as const,
		history: (eventId: string) =>
			[...queryKeys.events.all, "history", eventId] as const,
		/** Newest event for a single sensor (map marker popup). */
		latestBySensor: (sensorId: string) =>
			[...queryKeys.events.all, "latest-by-sensor", sensorId] as const,
		listPaginated: (filters?: EventFilters, limit?: number) =>
			[
				...queryKeys.events.listPaginatedWithoutLimit(filters).slice(0, -1),
				{ filters, limit },
			] as const,
		/** Special case for loading state detection - exclude limit (page size) from the key.
		 * This allows us to match the query key for the first page of results,
		 * which is what we need to determine if the table is loading.
		 * We can't use the normal listPaginated key because the limit is part of the key,
		 * and it will match the query key for the second page of results,
		 * which is what we don't want.
		 */
		listPaginatedWithoutLimit: (filters?: EventFilters) =>
			[...queryKeys.events.all, "list", "paginated", { filters }] as const,
		lists: () => [...queryKeys.events.all, "list"] as const,
		notifications: (eventId: string) =>
			[...queryKeys.events.all, "notifications", eventId] as const,
	},
	exports: {
		all: ["exports"] as const,
		list: () => [...queryKeys.exports.all, "list"] as const,
	},
	installations: {
		all: ["installations"] as const,
		lists: () => [...queryKeys.installations.all, "list"] as const,
	},
	locations: {
		all: ["locations"] as const,
		detail: (id: string) => [...queryKeys.locations.details(), id] as const,
		details: () => [...queryKeys.locations.all, "detail"] as const,
		list: (filters?: Record<string, unknown>) =>
			[...queryKeys.locations.lists(), { filters }] as const,
		listPaginated: () =>
			[...queryKeys.locations.all, "list", "paginated"] as const,
		lists: () => [...queryKeys.locations.all, "list"] as const,
	},
	me: {
		all: ["me"] as const,
	},
	/**
	 * Kept apart from `events` on purpose: event cache helpers walk the
	 * `events` keys expecting event-shaped data.
	 */
	reanalysis: {
		all: ["reanalysis"] as const,
		classes: () => [...queryKeys.reanalysis.all, "classes"] as const,
		view: (eventId: string) =>
			[...queryKeys.reanalysis.all, "view", eventId] as const,
	},
	sensors: {
		all: ["sensors"] as const,
		byLocation: (locationId: string) =>
			[...queryKeys.sensors.all, "location", locationId] as const,
		byLocations: (locationIds: string[]) =>
			[...queryKeys.sensors.all, "locations", ...locationIds] as const,
		detail: (id: string) => [...queryKeys.sensors.details(), id] as const,
		details: () => [...queryKeys.sensors.all, "detail"] as const,
		lists: () => [...queryKeys.sensors.all, "list"] as const,
		liveStatus: () => [...queryKeys.sensors.all, "live-status"] as const,
		summary: () => [...queryKeys.sensors.all, "summary"] as const,
	},
	users: {
		all: ["users"] as const,
		detail: (id: string) => [...queryKeys.users.details(), id] as const,
		details: () => [...queryKeys.users.all, "detail"] as const,
		lists: () => [...queryKeys.users.all, "list"] as const,
	},
	watches: {
		all: ["watches"] as const,
		detail: (id: string) => [...queryKeys.watches.details(), id] as const,
		details: () => [...queryKeys.watches.all, "detail"] as const,
		listPaginated: (filters?: WatchFilters, limit?: number) =>
			[
				...queryKeys.watches.all,
				"list",
				"paginated",
				{ filters, limit },
			] as const,
	},
	zones: {
		all: ["zones"] as const,
		byLocation: (locationId: string) =>
			[...queryKeys.zones.all, "location", locationId] as const,
		detail: (id: string) => [...queryKeys.zones.details(), id] as const,
		details: () => [...queryKeys.zones.all, "detail"] as const,
		lists: () => [...queryKeys.zones.all, "list"] as const,
	},
} as const;
